import { Pipe, PipeTransform, inject } from '@angular/core';
import { MenuItem } from 'primeng/api';
import { Params } from '@angular/router';
import { TableItemAction } from '../models/table-item-action';
import {
  HtmlAnchorTarget,
  isNil,
  Nillable,
  valueOrUndefined,
} from '@web/utils';
import { IconCssProviderService } from '../../icon/providers/icon-css-provider.service';
import { RouterLink } from '@web/common';

@Pipe({
  name: 'itemActionSubmenuItems',
  standalone: true,
})
export class ItemActionSubmenuItemsPipe implements PipeTransform {
  private readonly iconProvider = inject(IconCssProviderService);

  public transform<Item, Key>(
    actions: Nillable<Array<TableItemAction<Item, Key>>>,
    item: Item,
  ): MenuItem[] {
    if (isNil(actions)) {
      return [];
    }

    return this.mapActionsToMenuItems(actions, item);
  }

  private mapActionsToMenuItems<Item, Key>(
    actions: Array<TableItemAction<Item, Key>>,
    item: Item,
  ): MenuItem[] {
    return actions.map((action) => this.mapActionToMenuItem(action, item));
  }

  private mapActionToMenuItem<Item, Key>(
    action: TableItemAction<Item, Key>,
    item: Item,
  ): MenuItem {
    return {
      label: action.tooltip,
      icon: this.iconProvider.getCss(action.icon),
      target: valueOrUndefined(this.prepareTarget(action)),
      command: valueOrUndefined(this.prepareCommand(action, item)),
      url: valueOrUndefined(this.prepareUrl(action, item)),
      routerLink: this.prepareRouterLink(action, item),
      styleClass: this.prepareStyleClass(action),
      queryParams: valueOrUndefined(this.prepareQueryParams(action, item)),
    };
  }

  private prepareTarget<Item, Key>(
    action: TableItemAction<Item, Key>,
  ): Nillable<HtmlAnchorTarget> {
    if (!action.target) {
      return;
    }

    return action.target;
  }

  private prepareCommand<Item, Key>(
    action: TableItemAction<Item, Key>,
    item: Item,
  ): Nillable<() => void> {
    if (!action.isCommandType()) {
      return;
    }

    return () => action.command!(item);
  }

  private prepareQueryParams<Item, Key>(
    action: TableItemAction<Item, Key>,
    item: Item,
  ): Nillable<Params> {
    if (!action.isRouterLinkType()) {
      return;
    }

    if (isNil(action.queryParams)) {
      return;
    }

    return action.queryParams(item);
  }

  private prepareRouterLink<Item, Key>(
    action: TableItemAction<Item, Key>,
    item: Item,
  ): Nillable<RouterLink> {
    if (!action.isRouterLinkType()) {
      return;
    }

    return action.routerLink!(item);
  }

  private prepareStyleClass<Item, Key>(
    action: TableItemAction<Item, Key>,
  ): string {
    switch (action.severity) {
      case 'danger':
        return 'table-item-action--danger';

      default:
        return '';
    }
  }

  private prepareUrl<Item, Key>(
    action: TableItemAction<Item, Key>,
    item: Item,
  ): Nillable<string> {
    if (!action.isUrlType()) {
      return;
    }

    return action.url!(item);
  }
}
